import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  Plus,
  Search,
  MapPin,
  Edit,
  Trash2,
  Eye,
  Phone,
  Mail,
  Globe,
  Users,
  Building2,
  Star,
  ArrowLeft
} from 'lucide-react'
import { supabase, Location } from '@/lib/supabase'
import { LocationEditor } from '@/features/locations/components/LocationEditor'
import { ContentPreview } from '@/components/ui/content-preview'

// Database operations
const fetchLocations = async (): Promise<Location[]> => {
  const { data, error } = await supabase
    .from('locations')
    .select('*')
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

const updateLocationStatus = async (id: string, status: string): Promise<void> => {
  const { error } = await supabase
    .from('locations')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', id);

  if (error) throw error;
};

const deleteLocation = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('locations')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

export default function LocationsManagement() {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft' | 'archived'>('all')
  const [currentView, setCurrentView] = useState<'list' | 'editor' | 'preview'>('list')
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null)
  const [previewLocation, setPreviewLocation] = useState<Location | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  const queryClient = useQueryClient();

  // Fetch locations from database
  const { data: locations = [], isLoading, error } = useQuery({
    queryKey: ['locations'],
    queryFn: fetchLocations
  });

  // Update location status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      updateLocationStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    }
  });

  // Delete location mutation
  const deleteMutation = useMutation({
    mutationFn: deleteLocation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    }
  });

  const filteredLocations = locations.filter(location => {
    const matchesSearch = location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      location.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      location.slug.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || location.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const getStatusColor = (status: Location['status']) => {
    switch (status) {
      case 'published': return 'bg-chart-5/10 text-chart-5'
      case 'draft': return 'bg-muted text-muted-foreground'
      case 'archived': return 'bg-destructive/10 text-destructive'
      default: return 'bg-muted text-muted-foreground'
    }
  }

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this location? This action cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  }

  const handleStatusToggle = (id: string, currentStatus: Location['status']) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    updateStatusMutation.mutate({ id, status: newStatus });
  }

  const handleNewLocation = () => {
    setSelectedLocation(null);
    setCurrentView('editor');
  }

  const handleEditLocation = (location: Location) => {
    setSelectedLocation(location);
    setCurrentView('editor');
  }

  const handleViewLocation = (location: Location) => {
    setPreviewLocation(location);
    setIsPreviewOpen(true);
  }

  const handleEditorSave = (location: Location) => {
    setCurrentView('list');
    setSelectedLocation(null);
  }

  const handleEditorCancel = () => {
    setCurrentView('list');
    setSelectedLocation(null);
  }

  const handleEditorPreview = (location: Location) => {
    setPreviewLocation(location);
    setIsPreviewOpen(true);
  }

  const stats = {
    total: locations.length,
    published: locations.filter(l => l.status === 'published').length,
    draft: locations.filter(l => l.status === 'draft').length,
    primary: locations.filter(l => l.is_primary).length
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-destructive mb-2">Error Loading Locations</h2>
          <p className="text-gray-600">Unable to load locations data. Please try again later.</p>
        </div>
      </div>
    );
  }

  // Show editor view
  if (currentView === 'editor') {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={handleEditorCancel}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Locations
          </Button>
        </div>
        <LocationEditor
          location={selectedLocation || undefined}
          onSave={handleEditorSave}
          onCancel={handleEditorCancel}
          onPreview={handleEditorPreview}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Locations Management</h1>
          <p className="text-muted-foreground">
            Manage service locations, coverage areas, and franchise operations.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleNewLocation}>
            <Plus className="mr-2 h-4 w-4" />
            Add Location
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Locations</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">
              Service coverage areas
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Published</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.published}</div>
            <p className="text-xs text-muted-foreground">
              Live on website
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Draft</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.draft}</div>
            <p className="text-xs text-muted-foreground">
              In development
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Primary</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.primary}</div>
            <p className="text-xs text-muted-foreground">
              Head office locations
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle>Location Directory</CardTitle>
          <CardDescription>
            Manage franchise locations, service areas, and contact information.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-1 gap-2">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search locations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="px-3 py-2 border border-input rounded-md bg-background text-sm"
              >
                <option value="all">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Locations Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Location</TableHead>
                <TableHead>Parent/Region</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Primary</TableHead>
                <TableHead>Hero Image</TableHead>
                <TableHead>Reviews</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLocations.map((location) => (
                <TableRow key={location.id}>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="font-medium flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        {location.name}
                      </div>
                      <div className="text-sm text-muted-foreground line-clamp-2 max-w-xs">
                        {location.description.substring(0, 100)}...
                      </div>
                      <div className="text-xs text-muted-foreground">
                        /{location.slug}
                      </div>
                      {location.custom_title && (
                        <div className="text-xs text-primary font-medium">
                          {location.custom_title}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {location.parent_id ? (
                        <div className="text-sm">
                          Has parent region
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          Root location
                        </div>
                      )}
                      {location.meta_title && (
                        <div className="text-xs text-muted-foreground">
                          SEO: {location.meta_title.substring(0, 30)}...
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(location.status)}>
                      {location.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {location.is_primary ? (
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 text-accent fill-current" />
                        <span className="text-sm">Primary</span>
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {location.hero_image ? (
                      <div className="text-sm text-green-600">
                        ✓ Has hero
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        No hero image
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {location.review_ids ? location.review_ids.length : 0} reviews
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-muted-foreground">
                      {new Date(location.updated_at).toLocaleDateString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleViewLocation(location)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditLocation(location)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => handleDelete(location.id)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Service Area Coverage Map */}
      <Card>
        <CardHeader>
          <CardTitle>Service Area Coverage</CardTitle>
          <CardDescription>
            Geographic distribution of service locations and coverage areas.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {/* Ontario Regions */}
            <div className="space-y-2">
              <h4 className="font-medium">Greater Toronto Area</h4>
              <div className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span>Toronto Central</span>
                  <Badge variant="outline" className="text-green-600">Active</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>North York</span>
                  <Badge variant="outline">Planned</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Etobicoke</span>
                  <Badge variant="outline">Planned</Badge>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Greater Hamilton Area</h4>
              <div className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span>Hamilton</span>
                  <Badge variant="outline" className="text-green-600">Active</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Burlington</span>
                  <Badge variant="outline">Planned</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Stoney Creek</span>
                  <Badge variant="outline">Planned</Badge>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Other Regions</h4>
              <div className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span>London</span>
                  <Badge variant="outline" className="text-blue-600">New - 2025</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Kitchener</span>
                  <Badge variant="outline">Under Review</Badge>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Location Management Tools</CardTitle>
          <CardDescription>
            Quick access to common location management tasks.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Desktop grid */}
          <div className="hidden sm:grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Button variant="outline" className="h-auto p-4 justify-start" onClick={handleNewLocation}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                  <Plus className="h-4 w-4" />
                </div>
                <div className="text-left">
                  <div className="font-medium">Add Location</div>
                  <div className="text-sm text-muted-foreground">Create new service area</div>
                </div>
              </div>
            </Button>

            <Button variant="outline" className="h-auto p-4 justify-start">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 text-green-600">
                  <MapPin className="h-4 w-4" />
                </div>
                <div className="text-left">
                  <div className="font-medium">Map View</div>
                  <div className="text-sm text-muted-foreground">View location coverage</div>
                </div>
              </div>
            </Button>

            <Button variant="outline" className="h-auto p-4 justify-start">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100 text-purple-600">
                  <Building2 className="h-4 w-4" />
                </div>
                <div className="text-left">
                  <div className="font-medium">Franchise Setup</div>
                  <div className="text-sm text-muted-foreground">Configure new franchise</div>
                </div>
              </div>
            </Button>

            <Button variant="outline" className="h-auto p-4 justify-start">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                  <Globe className="h-4 w-4" />
                </div>
                <div className="text-left">
                  <div className="font-medium">SEO Review</div>
                  <div className="text-sm text-muted-foreground">Optimize local SEO</div>
                </div>
              </div>
            </Button>
          </div>
          {/* Mobile horizontal scroll */}
          <div className="sm:hidden">
            <ScrollArea className="w-full">
              <div className="flex gap-3 pb-2">
                <Button variant="outline" className="min-w-[220px] h-auto p-3 justify-start" onClick={handleNewLocation}>
                  <div className="p-2 rounded-lg bg-blue-100 text-blue-600 mr-3">
                    <Plus className="h-4 w-4" />
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-medium leading-tight">Add Location</div>
                    <div className="text-xs text-muted-foreground">Create new service area</div>
                  </div>
                </Button>
                <Button variant="outline" className="min-w-[220px] h-auto p-3 justify-start">
                  <div className="p-2 rounded-lg bg-green-100 text-green-600 mr-3">
                    <MapPin className="h-4 w-4" />
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-medium leading-tight">Map View</div>
                    <div className="text-xs text-muted-foreground">View location coverage</div>
                  </div>
                </Button>
                <Button variant="outline" className="min-w-[220px] h-auto p-3 justify-start">
                  <div className="p-2 rounded-lg bg-purple-100 text-purple-600 mr-3">
                    <Building2 className="h-4 w-4" />
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-medium leading-tight">Franchise Setup</div>
                    <div className="text-xs text-muted-foreground">Configure new franchise</div>
                  </div>
                </Button>
                <Button variant="outline" className="min-w-[220px] h-auto p-3 justify-start">
                  <div className="p-2 rounded-lg bg-orange-100 text-orange-600 mr-3">
                    <Globe className="h-4 w-4" />
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-medium leading-tight">SEO Review</div>
                    <div className="text-xs text-muted-foreground">Optimize local SEO</div>
                  </div>
                </Button>
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {previewLocation?.name || 'Location Preview'}
            </DialogTitle>
          </DialogHeader>
          {previewLocation && (
            <div className="space-y-6">
              {/* Location Hero */}
              <div className="border-b pb-6">
                {previewLocation.hero_image && (
                  <div className="aspect-video rounded-lg overflow-hidden mb-4">
                    <img
                      src={previewLocation.hero_image}
                      alt={previewLocation.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                <div className="space-y-4">
                  {previewLocation.custom_title && (
                    <h2 className="text-2xl font-bold text-blue-600">
                      {previewLocation.custom_title}
                    </h2>
                  )}

                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                    <Badge className={getStatusColor(previewLocation.status)}>
                      {previewLocation.status}
                    </Badge>
                    {previewLocation.is_primary && (
                      <Badge variant="outline" className="text-yellow-600">
                        <Star className="w-3 h-3 mr-1" />
                        Primary Location
                      </Badge>
                    )}
                    {previewLocation.franchise_region && (
                      <Badge variant="secondary">{previewLocation.franchise_region}</Badge>
                    )}
                  </div>

                  <p className="text-lg text-muted-foreground">
                    {previewLocation.description}
                  </p>
                </div>
              </div>

              {/* Contact Information */}
              {(previewLocation.address_line1 || previewLocation.phone || previewLocation.email) && (
                <div className="grid gap-6 md:grid-cols-2">
                  {(previewLocation.address_line1 || previewLocation.city) && (
                    <div>
                      <h3 className="text-xl font-semibold mb-3">Address</h3>
                      <div className="space-y-1 text-sm">
                        {previewLocation.address_line1 && <div>{previewLocation.address_line1}</div>}
                        {previewLocation.address_line2 && <div>{previewLocation.address_line2}</div>}
                        {previewLocation.city && (
                          <div>
                            {previewLocation.city}
                            {previewLocation.province && `, ${previewLocation.province}`}
                            {previewLocation.postal_code && ` ${previewLocation.postal_code}`}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="text-xl font-semibold mb-3">Contact</h3>
                    <div className="space-y-2 text-sm">
                      {previewLocation.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          {previewLocation.phone}
                        </div>
                      )}
                      {previewLocation.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          {previewLocation.email}
                        </div>
                      )}
                    </div>

                    {previewLocation.hours && (
                      <div className="mt-4">
                        <h4 className="font-medium mb-2">Business Hours</h4>
                        <div className="text-sm whitespace-pre-line text-muted-foreground">
                          {previewLocation.hours}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Area Description */}
              {previewLocation.area_description && (
                <div>
                  <h3 className="text-xl font-semibold mb-4">Service Area</h3>
                  <ContentPreview
                    content={previewLocation.area_description}
                    showBorder={false}
                    maxHeight="none"
                    className="prose-lg"
                  />
                </div>
              )}

              {/* Services Offered */}
              {previewLocation.services_offered && previewLocation.services_offered.length > 0 && (
                <div>
                  <h3 className="text-xl font-semibold mb-4">Services Offered</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    {previewLocation.services_offered.map((service) => (
                      <div key={service.id} className="p-4 border rounded-lg">
                        <h4 className="font-medium mb-2">{service.service_name}</h4>
                        <p className="text-sm text-muted-foreground">{service.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* FAQs */}
              {previewLocation.faqs && previewLocation.faqs.length > 0 && (
                <div>
                  <h3 className="text-xl font-semibold mb-4">Frequently Asked Questions</h3>
                  <div className="space-y-4">
                    {previewLocation.faqs.map((faq) => (
                      <div key={faq.id} className="border-l-2 border-blue-200 pl-4">
                        <h4 className="font-medium mb-2">{faq.question}</h4>
                        <p className="text-sm text-muted-foreground">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
